import React, { useState } from "react";
import {
  Card,
  Button,
  DatePicker,
  Form,
  Space,
  Alert,
  Typography,
  Divider,
  Row,
  Col,
  message,
  Tag,
} from "antd";
import {
  PlayCircleOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import SchedulerService from "../services/scheduler.service";

const { Title, Text, Paragraph } = Typography;

interface TriggerResult {
  success: boolean;
  message: string;
  timestamp: string;
}

/**
 * 手工触发随访安排页面
 */
const SchedulerTrigger: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState<TriggerResult | null>(null);

  /**
   * 处理触发自动排任务
   */
  const handleTrigger = async (values: { targetDate?: Dayjs }) => {
    setLoading(true);
    try {
      const targetDate = values.targetDate
        ? SchedulerService.formatDate(values.targetDate.toDate())
        : undefined;

      const result = await SchedulerService.triggerAutoSchedule({
        targetDate,
      });

      const triggerResult: TriggerResult = {
        success: result.success,
        message: result.message,
        timestamp: new Date().toLocaleString("zh-CN"),
      };

      setLastResult(triggerResult);

      if (result.success) {
        message.success("自动排任务触发成功！");
      } else {
        message.error("自动排任务触发失败！");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "未知错误";
      const triggerResult: TriggerResult = {
        success: false,
        message: errorMessage,
        timestamp: new Date().toLocaleString("zh-CN"),
      };
      setLastResult(triggerResult);
      message.error("触发失败：" + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 清空表单
   */
  const handleReset = () => {
    form.resetFields();
    setLastResult(null);
  };

  // 禁用过去的日期（不包括今天）
  const disabledDate = (current: Dayjs) => {
    return current && current < dayjs().startOf("day");
  };

  return (
    <div style={{ padding: "24px" }}>
      <Title level={2}>
        <PlayCircleOutlined style={{ marginRight: "8px", color: "#1890ff" }} />
        手工触发随访安排
      </Title>

      <Row gutter={[24, 24]}>
        {/* 功能说明卡片 */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <InfoCircleOutlined style={{ color: "#1890ff" }} />
                功能说明
              </Space>
            }
            size="small"
          >
            <Paragraph>
              <Text strong>自动排任务功能：</Text>
            </Paragraph>
            <ul style={{ paddingLeft: "20px", margin: "8px 0" }}>
              <li>系统会自动为指定日期安排随访任务</li>
              <li>正常情况下，系统在工作日下午2点自动执行</li>
              <li>此功能用于测试或紧急情况下的手动触发</li>
              <li>如果不指定日期，默认为今天</li>
            </ul>

            <Alert
              message="注意事项"
              description="请谨慎使用此功能，建议使用前，先确认系统是否确实遗漏了任务安排。"
              type="warning"
              showIcon
              style={{ marginTop: "16px" }}
            />
          </Card>
        </Col>

        {/* 操作区域 */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <CalendarOutlined style={{ color: "#52c41a" }} />
                触发操作
              </Space>
            }
            size="small"
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleTrigger}
              initialValues={{
                targetDate: dayjs(SchedulerService.getTodayDate()),
              }}
            >
              <Form.Item
                label="目标日期"
                name="targetDate"
                tooltip="选择要安排随访任务的日期，留空则默认为今天"
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="选择目标日期（可选）"
                  disabledDate={disabledDate}
                  format="YYYY-MM-DD"
                />
              </Form.Item>

              <Form.Item>
                <Space size="middle">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    icon={<PlayCircleOutlined />}
                  >
                    触发自动排任务
                  </Button>

                  <Button onClick={handleReset}>重置</Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>

      {/* 执行结果 */}
      {lastResult && (
        <Card
          title={
            <Space>
              {lastResult.success ? (
                <CheckCircleOutlined style={{ color: "#52c41a" }} />
              ) : (
                <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
              )}
              执行结果
            </Space>
          }
          style={{ marginTop: "24px" }}
          size="small"
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Text strong>执行状态：</Text>
              <Tag color={lastResult.success ? "success" : "error"}>
                {lastResult.success ? "成功" : "失败"}
              </Tag>
            </Col>
            <Col xs={24} sm={8}>
              <Text strong>执行时间：</Text>
              <Text>{lastResult.timestamp}</Text>
            </Col>
            <Col xs={24}>
              <Text strong>执行结果：</Text>
              <div style={{ marginTop: "8px" }}>
                <Alert
                  message={lastResult.message}
                  type={lastResult.success ? "success" : "error"}
                  showIcon
                />
              </div>
            </Col>
          </Row>
        </Card>
      )}

      <Divider />

      {/* 系统状态信息 */}
      <Card title="系统信息" size="small" style={{ marginTop: "16px" }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Text strong>自动执行时间：</Text>
            <Text>工作日下午2点（14:00）</Text>
          </Col>
          <Col xs={24} sm={12}>
            <Text strong>时区：</Text>
            <Text>Asia/Shanghai</Text>
          </Col>
          <Col xs={24} sm={12}>
            <Text strong>默认目标日期：</Text>
            <Text>
              今天（
              {SchedulerService.formatDate(SchedulerService.getTodayDate())}）
            </Text>
          </Col>
          <Col xs={24} sm={12}>
            <Text strong>当前时间：</Text>
            <Text>{new Date().toLocaleString("zh-CN")}</Text>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default SchedulerTrigger;
